import { authSessionSchema } from '@secretario/shared';
import { clearSession, readSession, renewDevice, writeSession } from '../session.js';

/** No site, a API fica na mesma origem (`/api`); no app instalado, na URL completa do servidor. */
export const API_BASE: string = import.meta.env.VITE_API_BASE_URL || '/api';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  auth?: boolean;
  /** O pedido segue mesmo se a página for fechada logo depois (avisos de saída). */
  keepalive?: boolean;
};

const GENERIC_MESSAGE = 'Não consegui falar com o servidor agora. Tenta de novo em instantes.';

async function toError(response: Response): Promise<ApiError> {
  try {
    const payload = (await response.json()) as { error?: { code?: string; message?: string } };
    // O servidor pediu a permissão de IA: a folha abre onde a pessoa estiver.
    if (payload.error?.code === 'ai_consent_required') {
      window.dispatchEvent(new Event('secretario:permissao-ia'));
    }
    return new ApiError(
      response.status,
      payload.error?.code ?? 'request_failed',
      payload.error?.message ?? GENERIC_MESSAGE,
    );
  } catch {
    return new ApiError(response.status, 'request_failed', GENERIC_MESSAGE);
  }
}

type Renewal = 'renewed' | 'refused' | 'failed';
let refreshing: Promise<Renewal> | null = null;

/** Renova a sessão uma vez por vez; chamadas paralelas esperam a mesma renovação. */
async function refreshSession(): Promise<Renewal> {
  if (refreshing) return refreshing;
  refreshing = (async (): Promise<Renewal> => {
    const current = readSession();
    if (!current) return 'refused';
    try {
      const response = await fetch(`${API_BASE}/v1/auth/session/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken: current.session.refreshToken }),
      });
      if (!response.ok) {
        // Só a recusa da sessão encerra a conta neste aparelho. Servidor fora do ar,
        // limite de pedidos ou rede ruim não desconectam ninguém: tenta de novo depois.
        if (response.status === 401 || response.status === 403) {
          clearSession();
          return 'refused';
        }
        return 'failed';
      }
      writeSession(authSessionSchema.parse(await response.json()));
      return 'renewed';
    } catch {
      return 'failed';
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, auth = true } = options;

  async function attempt(): Promise<Response> {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (auth) {
      const session = readSession();
      if (session) headers.authorization = `Bearer ${session.session.accessToken}`;
    }
    const init: RequestInit = { method, headers };
    if (options.keepalive) init.keepalive = true;
    if (body !== undefined) init.body = JSON.stringify(body);
    return fetch(`${API_BASE}${path}`, init);
  }

  let response: Response;
  try {
    response = await attempt();
    if (auth && response.status === 401 && readSession()) {
      const renewal = await refreshSession();
      if (renewal === 'renewed') response = await attempt();
      // Não deu para renovar agora (rede ou servidor): a sessão continua valendo.
      else if (renewal === 'failed') throw new ApiError(0, 'network', GENERIC_MESSAGE);
    }
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(0, 'network', GENERIC_MESSAGE);
  }

  if (!response.ok) {
    const error = await toError(response);
    if (auth && error.status === 401) clearSession();
    throw error;
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

/**
 * Pedido com corpo ou resposta em binário (arquivos guardados). Mesma sessão,
 * mesma renovação e os mesmos erros de `apiFetch`.
 */
export async function apiBinary(
  path: string,
  options: { method?: 'GET' | 'POST' | 'PUT'; body?: Blob; headers?: Record<string, string> } = {},
): Promise<Response> {
  const { method = 'GET', body } = options;
  async function attempt(): Promise<Response> {
    const headers: Record<string, string> = { ...(options.headers ?? {}) };
    if (body) headers['content-type'] = body.type || 'application/octet-stream';
    const session = readSession();
    if (session) headers.authorization = `Bearer ${session.session.accessToken}`;
    return fetch(`${API_BASE}${path}`, { method, headers, ...(body ? { body } : {}) });
  }
  let response: Response;
  try {
    response = await attempt();
    if (response.status === 401 && readSession()) {
      const renewal = await refreshSession();
      if (renewal === 'renewed') response = await attempt();
      else if (renewal === 'failed') throw new ApiError(0, 'network', GENERIC_MESSAGE);
    }
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(0, 'network', GENERIC_MESSAGE);
  }
  if (!response.ok) {
    const error = await toError(response);
    if (error.status === 401) clearSession();
    throw error;
  }
  return response;
}

/** Código estável do erro da API (ex.: `city_missing`), ou null se não for um erro dela. */
export function errorCode(error: unknown): string | null {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === 'string' ? code : null;
}

export function describeError(error: unknown, fallback = GENERIC_MESSAGE): string {
  return error instanceof ApiError ? error.message : fallback;
}

/**
 * Pedido com resposta em fluxo (`text/event-stream`): POST com corpo, ou GET quando o
 * corpo é `undefined`. O que falha antes de o fluxo abrir (limite do plano, sessão)
 * vira `ApiError`; depois disso, cada evento chega em `onEvent` na ordem em que o
 * servidor enviou.
 */
export async function apiStream(
  path: string,
  body: unknown,
  onEvent: (data: unknown) => void,
  signal?: AbortSignal,
): Promise<void> {
  async function attempt(): Promise<Response> {
    const headers: Record<string, string> = { accept: 'text/event-stream' };
    if (body !== undefined) headers['content-type'] = 'application/json';
    const session = readSession();
    if (session) headers.authorization = `Bearer ${session.session.accessToken}`;
    return fetch(`${API_BASE}${path}`, {
      method: body === undefined ? 'GET' : 'POST',
      headers,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(signal ? { signal } : {}),
    });
  }

  let response: Response;
  try {
    response = await attempt();
    if (response.status === 401 && readSession()) {
      const renewal = await refreshSession();
      if (renewal === 'renewed') response = await attempt();
      else if (renewal === 'failed') throw new ApiError(0, 'network', GENERIC_MESSAGE);
    }
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(0, 'network', GENERIC_MESSAGE);
  }
  if (!response.ok || !response.body) {
    const error = await toError(response);
    if (error.status === 401) clearSession();
    throw error;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let boundary = buffer.indexOf('\n\n');
    while (boundary >= 0) {
      const frame = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      const data = frame
        .split('\n')
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).trimStart())
        .join('\n');
      if (data) {
        try {
          onEvent(JSON.parse(data));
        } catch {
          /* quadro incompleto ou comentário de keep-alive */
        }
      }
      boundary = buffer.indexOf('\n\n');
    }
  }
}

/**
 * Entrada numa conta (código, Google, chave de acesso, autenticador, recuperação). Se o
 * servidor responder que este aparelho já é de outra conta, o app troca a identidade do
 * aparelho e tenta uma vez de novo: o erro desfaz a transação no servidor, então o código
 * recebido continua valendo. `send` precisa ler `getDevice()` de novo a cada chamada.
 */
export async function withDevice<T>(send: () => Promise<T>): Promise<T> {
  try {
    return await send();
  } catch (error) {
    if (!(error instanceof ApiError) || error.code !== 'identity_unavailable') throw error;
    renewDevice();
    return send();
  }
}
